import { ElMessage } from 'element-plus'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import axios from 'axios'
import { MingTing } from '@/project-subject/subject-config/ming-ting/ming-ting'
import { i18n } from '@/i18n'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'

// 扩展 dayjs 以支持 UTC
dayjs.extend(utc)

interface DownloadByBlobParams {
  /** 文件 */
  file: Blob,
  /** 文件名 */
  name?: string,
  /** 文件类型 */
  type?: string
  /** 文件名是否拼接事件 */
  isJoinTime?: boolean
}

interface DownloadTxtParams {
  /** 数据 */
  text: string,
  /** 文件名 */
  name?: string,
  /** 文件名是否拼接事件 */
  isJoinTime?: boolean
}

interface GetTreeNodeParams {
  /** 数据 */
  tree: any,
  /** 指定字段 */
  field: string,
  /** 指定字段对应的值 */
  value: any,
  /** 子节点的字段名 */
  childrenField: string
}

/** 选项 */
interface Option {
  /** label */
  label: string,
  /** value */
  value: string | number
}

export function copyText(text: string) {
  const textDom = document.createElement('div')
  textDom.style.cssText = `
    position: fixed;
    top: -9999999px;
  `
  textDom.innerText = text
  document.body.appendChild(textDom)

  const range = document.createRange()
  range.selectNodeContents(textDom)
  const selection = document.getSelection()
  if (selection) {
    selection.removeAllRanges()
    selection.addRange(range)
    document.execCommand('copy')
    selection.removeRange(range)
    ElMessage.success(i18n.global.t('common.copySuccess'))
  }
  textDom.parentNode?.removeChild(textDom)
}

export function copyDomText(node: Node) {
  if (!node) {
    return
  }

  const range = document.createRange()
  range.selectNodeContents(node)
  const selection = document.getSelection()
  if (selection) {
    selection.removeAllRanges()
    selection.addRange(range)
    document.execCommand('copy')
    selection.removeRange(range)
    ElMessage.success(i18n.global.t('common.copySuccess'))
  }
}

/** 获取数据类型 */
export function getType(value: any) {
  const sliceIndex = 8
  return Object.prototype.toString.call(value).slice(sliceIndex, -1).toLowerCase()
}

export function generateTooltip(text = '') {
  const tooltip = document.createElement('div')
  tooltip.innerHTML = text
  tooltip.style.cssText = `
    position: fixed;
    background: var(--cm-color-card-bg);
    line-height: 20px;
    max-width: 500px;
    padding: 10px;
    z-index: 999999;
    word-break: break-all;
    display: none;
    border-radius: 15px;
    color: var(--cm-color-text-primary);
    box-shadow: rgb(0 0 0 / 20%) 1px 2px 10px;
  `
  document.body.appendChild(tooltip)
  return tooltip
}

export function getOpacityColor(color = '#000', opacity = 0.5) {
  opacity = Math.max(opacity, 0)
  opacity = Math.min(opacity, 1)
  color = color.replace(/\#/g, '').toUpperCase()
  if (color.length === 3) {
    let arr = color.split('')
    color = ''
    for (let i = 0; i < arr.length; i++) {
      color += (arr[i] + arr[i])//将简写的3位字符补全到6位字符
    }
  }
  let num = Math.round(255 * opacity)//四舍五入
  let str = ''
  let arrHex = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F']//十六进制数组
  while (num > 0) {
    let mod = num % 16
    num = (num - mod) / 16
    str = arrHex[mod] + str
  }
  if (str.length == 1) str = '0' + str
  if (str.length == 0) str = '00'
  return `#${color + str}`
}

export function formatOptions(valueMap, labelMap) {
  const options = [] as Option[]
  Object.keys(labelMap).forEach((key) => {
    options.push({ label: labelMap[key], value: valueMap[key] })
  })
  return options
}

/** 关系映射转为下拉选项options */
export function formatMapToOptions(mapObject: any, isNumberKey?: boolean): Option[] {
  return Object.keys(mapObject).map(key => ({
    label: mapObject[key],
    value: isNumberKey ? ~~key : key,
    // children: mapObject[key] instanceof Object? formatMapToOptions(mapObject[key], isNumberKey) : [] // 若是对象，递归转换
  }))
}

/** 合并映射表中相同值的键为序列化数组形式 */
export function mergeMap(mapObject) {
  const mergedMap = {}
  const valueKeyMap = {}

  for (const [key, value] of Object.entries(mapObject) as any) {
    if (!valueKeyMap[value]) {
      valueKeyMap[value] = []
    }
    valueKeyMap[value].push(deserialize(key))
  }

  for (const [label, keys] of Object.entries(valueKeyMap)) {
    mergedMap[`[${keys}]`] = label
  }

  return mergedMap
}

/** 反序列化 */
export function deserialize(str) {
  if (typeof str !== 'string') return str
  try {
    return JSON.parse(str)
  } catch {
    return str
  }
}

/**
 * 通过某一字段获取树形数据的节点
 */
export function getTreeNode(params: GetTreeNodeParams) {
  let result
  const getNode = (children) => {
    for (let i = 0; i < children?.length; i++) {
      const isTargetNode = children[i][params.field] === params.value
      if (isTargetNode) {
        result = children[i]
        return
      } if (children[i][params.childrenField]?.length) {
        getNode(children[i][params.childrenField])
      }
    }
  }
  getNode([params.tree])
  return result
}

export function downloadByUrl(url: string) {
  if (!url) return

  // 将[http://] [https://] 替换为[//] ，保证协议一致，不然无法下载
  url = url.replace(/^https?:/, '')
  // 不以//开头的URL，视为相对路径，拼接当前origin
  if (!/^\/\//.test(url)) {
    url = `${window.origin}/${url.replace(/^\/+/, '')}`
  }

  console.log('downloadByUrl', url)
  const elementA = document.createElement('a')
  elementA.href = url
  elementA.click()
}

export function downloadByBlob(data: DownloadByBlobParams) {
  if (data?.file?.type?.toLowerCase() === 'application/json') {
    ElMessage.error('下载失败，文件不存在')
    return
  }
  let { file, name = '导出文件', type = 'txt', isJoinTime } = data
  const fileUrl = window.URL.createObjectURL(file)
  const link = document.createElement('a')
  if (isJoinTime) {
    name += dayjs().format('YYYYMMDDHHmmss')
  }
  link.href = fileUrl
  const fileName = `${name}.${type}`
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export function downloadTxt (data: DownloadTxtParams) {
  let { text, name = '导出文件', isJoinTime } = data
  downloadByBlob({
    file: new Blob([text]),
    isJoinTime,
    name
  })
}

export function downloadALiYun(url) {
  const fullNameSplit = url.split('?')[0]?.split('/')?.pop().split('.')
  const type = fullNameSplit.pop()
  const name = decodeURIComponent(fullNameSplit.join(''))
  return axios.get(url, {
    responseType: 'blob'
  }).then((response) => {
    return downloadByBlob({
      file: response.data,
      name,
      type
    })
  })
}

export function isObjectEmpty(data: any) {
  return !data || JSON.stringify(data) === '{}'
}

export function isObjectFieldEmpty(data: any) {
  if (!data || JSON.stringify(data) === '{}') {
    return true
  }
  return !Object.values(data).find(v => v !== undefined || v !== '')
}

/**
 * 根据数组对象的某个字段去重
 */
export function unique(arr, field: string) {
  const res = new Map()
  return arr.filter(item => !res.has(item[field]) && res.set(item[field], 1))
}

/**
 * 根据数组对象的某个字段删除
 */
export function deleteByField(arr: any[], field: string, value: any) {
  const index = arr.findIndex(v => v[field] === value)
  if (index === -1) return arr
  arr.splice(index, 1)
  return arr
}
// 根据路径获取数据结构
export const getDataFromPath = (source: any, path: string) => {
  const pathArr = path.split('.')
  let result = source
  pathArr.forEach(item => {
    result = result?.[item]
  })
  return result
}

// 扁平化treeData
export const flattenArray = (tree, arr = []) => {
  let res = arr || [];
  tree.forEach(item => {
    let { children, ...attr } = item;
    res.push(attr); // 添加属性

    if (children && children.length) {
      // 递归将所有节点加入到结果集中
      flattenArray(children, res);
    }
  });
  return res;
}
// 校验数组中是否有重复值
export const getIsRepeat = (arr: any[]) => {
  var hash = {};
  for (var i in arr) {
    if (hash[arr[i]]) {
      // 找到重复值
      return true;
    }
    hash[arr[i]] = true;
  }
  return false;
}

export function getCookieDomain () {
  const hostname = window.location.hostname.toLowerCase()
  const singleTopDomain = ['.duba.net', '.testcluster-k8s-01.iweikan.cn']
  const currentTopDomain = singleTopDomain.find(v => hostname.endsWith(v))
  return currentTopDomain || hostname
}

export const COOKIE_CONFIG = {
  expires: 999,
  sameSite: 'Strict',
  priority: 'High',
  domain: getCookieDomain(),
}
// 删除对象中值为空的属性
export const parseIsNotEmptyObj = (obj: any) => {
  let result = {};
  for (let key in obj) {
    if (obj[key] !== '' && obj[key] !== null && obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
};
// 字节单位自适应转换
export const bytesToSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024,
    // sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'],//到GB即可，目前不需要这么大的单位，甚至到M
    i = Math.floor(Math.log(bytes) / Math.log(k));

  return (bytes / Math.pow(k, i)).toPrecision(3) + '' + sizes[i];
};

//匹配每一个关键字字符
export const highLightKeywords = (text: string, words: string, tag: string = 'span') => {
  let i, len = words.length, re;
  for (i = 0; i < len; i++) {
    // 正则匹配所有的文本
    re = new RegExp(words[i], 'g');
    if (re.test(text)) {
      text = text?.replace?.(re, `<la>$&</la>`);
    }
  }
  return text;
}
// 构造树形结构
export const handleDataToTree = (data:object[], id:string|number, parentId:string|number, children = 'children'):object[] => {
  const config = {
    id: id ?? 'id',
    parentId: parentId ?? 'parentId',
    childrenList: children ?? 'children'
  };

  let childrenListMap = {};
  let nodeIds = {};
  let tree = [];

  for (let d of data) {
    let parentId = d[config.parentId];
    if (!childrenListMap[parentId]) {
      childrenListMap[parentId] = [];
    }
    nodeIds[d[config.id]] = d;
    childrenListMap[parentId].push(d);
  }

  for (let d of data) {
    let parentId = d[config.parentId];
    if (!nodeIds[parentId]) {
      tree.push(d);
    }
  }

  for (let t of tree) {
    adaptToChildrenList(t);
  }

  function adaptToChildrenList(o) {
    if (childrenListMap[o[config.id]]) {
      o[config.childrenList] = childrenListMap[o[config.id]];
    }
    if (o[config.childrenList]) {
      for (let c of o[config.childrenList]) {
        adaptToChildrenList(c);
      }
    }
  }
  return tree;
}
export const  formatHoursToHoursMinutes = (hours:number) =>  {
  const minutes = hours * 60;
  const hoursFormatted = Math.floor(minutes / 60).toString().padStart(2, '0');
  const minutesFormatted = (minutes % 60).toString().padStart(2, '0');
  return `${hoursFormatted}:${minutesFormatted}`;
}

/**
 * 判断当前版本是否大于或等于目标版本。
 * 版本号按照“x.x.x.x.x.x.x...”格式解析，不足部分视为 0 比较。
 * @param current - 当前版本号字符串，例如 "1.0.0.137"
 * @param target  - 目标版本号字符串，例如 "1.0.1"
 * @returns 如果 current ≥ target 则返回 true，否则返回 false
 */
export function isVersionGte (current: string, target: string): boolean {
  if (!current) {
    current = '0'
  }
  const currParts = current.split('.').map(p => parseInt(p, 10) || 0)
  const targetParts = target.split('.').map(p => parseInt(p, 10) || 0)
  const length = Math.max(currParts.length, targetParts.length)

  for (let i = 0; i < length; i++) {
    const currNum = currParts[i] ?? 0
    const targetNum = targetParts[i] ?? 0
    if (currNum > targetNum) {
      return true
    }
    if (currNum < targetNum) {
      return false
    }
    // 如果相等，则继续下一段比较
  }

  // 所有段都相等
  return true
}

export function getListShow (params: {
  /** 需要处理的字符串列表（必填） */
  list: string[];
  /** 数量单位（默认值：'个'），用于显示总数时的后缀（如“共5个”） */
  unit?: string;
  /** 最多显示的列表项数量（默认值：1），超过此数量则显示省略 */
  max?: number;
  /** 列表项之间的分隔符（默认值：'，'），如“项1，项2”中的分隔符 */
  separator?: string;
  /** 列表为空时显示的默认内容（默认值：'--'） */
  empty?: string;
}) {
  const { list = [], unit = '个', max = 1, separator = '，', empty = '--' } = params
  const listStr = list.slice(0, max).join(separator)
  if (list.length === 0) {
    return empty
  }
  if (list.length <= max) {
    return listStr
  }
  if (list.length > max) {
    return listStr + `...共${list.length}${unit}`
  }
}

export function formatWindowsFileTime(windowsFileTime, format = 'YYYY-MM-DD HH:mm:ss', timezoneOffset = 8) {
    // 直接转换为数字（字符串直接除）
    const winTimeNum = typeof windowsFileTime === 'number'
        ? windowsFileTime
        : parseFloat(windowsFileTime);

    // 转换为 Unix 时间戳（秒）
    const unixSeconds = winTimeNum / 10000000 - 11644473600;

    // 直接计算目标时区的时间（毫秒）
    const targetTimeMs = unixSeconds * 1000 + timezoneOffset * 3600000;
    const date = new Date(targetTimeMs);

    // 获取日期时间组件
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');

    // 获取毫秒和微秒
    const milliseconds = Math.floor((unixSeconds % 1) * 1000000);
    const msFull = String(date.getUTCMilliseconds()).padStart(3, '0');

    // 格式化字符串
    let result = format
        .replace('YYYY', year.toString())
        .replace('MM', month)
        .replace('DD', day)
        .replace('HH', hours)
        .replace('mm', minutes)
        .replace('ss', seconds)
        .replace('SSS', msFull)
        .replace('SSSSSS', String(milliseconds).padStart(6, '0'));

    if (format.includes('uuu')) {
        const microseconds = String(milliseconds % 1000).padStart(3, '0');
        result = result.replace('uuu', microseconds);
    }

    return result;
}
export async function hmac256(message, secret = MingTing.sign_key) {
  const enc = new TextEncoder();

  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    enc.encode(message)
  );

  // ArrayBuffer → hex
  return Array.from(new Uint8Array(signature))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export const MAX_LENGTH = IS_JP_ONLY_BUILD ? 50 : 30

export const splitSoftHashKey = (softHashKey?: string): string[] => {
  return softHashKey
    ? softHashKey.split(',').map(item => item.trim()).filter(Boolean)
    : []
}
